import type { ClientId, EquipmentId } from "../shared/kernel/identifiers.js";
import { AppError, ErrorCode } from "../shared/errors/app-error.js";

/**
 * HIERARQUIA DOS ATIVOS TÉCNICOS (INSTRUMENTO, DISPOSITIVO DE SEGURANÇA).
 *
 * CLIENTE → PROJETO → UNIDADE / ÁREA → EQUIPAMENTO.
 *
 * O Instrumento e a PSV podem existir SEM equipamento ("Não vinculado a
 * equipamento específico"), por isso o Cliente é deles — gravado no próprio
 * registro — e nunca derivado do equipamento. O que vem da tela (Projeto,
 * Unidade/Área, Equipamento) é conferido aqui, no serviço: a tela filtra as
 * opções, mas é o backend que recusa a combinação incompatível.
 *
 * A Unidade / Área é a do PROJETO (a mesma do cadastro de Equipamento), e
 * fica gravada no campo `areaId` do ativo.
 */
export interface TechnicalAssetHierarchyLookup {
  getProject(projectId: string): Promise<{ clientId: string } | null>;
  getUnitArea(unitAreaId: string): Promise<{ clientId: string; projectId: string } | null>;
  /** Equipamento no escopo do Cliente — `null` quando não existe NESTE Cliente. */
  getEquipment(clientId: ClientId, equipmentId: EquipmentId): Promise<TechnicalAssetEquipmentRef | null>;
}

export interface TechnicalAssetEquipmentRef {
  clientId: string;
  projectId?: string | null;
  unitAreaId?: string | null;
  areaId?: string | null;
}

export interface TechnicalAssetLinks {
  clientId: string | null | undefined;
  projectId?: string | null | undefined;
  areaId?: string | null | undefined;
  equipmentId?: string | null | undefined;
}

export const SELECT_CLIENT_MESSAGE = "Selecione o Cliente do cadastro.";
export const PROJECT_OF_OTHER_CLIENT_MESSAGE = "O Projeto selecionado não pertence ao Cliente informado.";
export const AREA_OF_OTHER_CLIENT_MESSAGE = "A Unidade / Área selecionada não pertence ao Cliente informado.";
export const AREA_OF_OTHER_PROJECT_MESSAGE = "A Unidade / Área selecionada não pertence ao Projeto informado.";
export const EQUIPMENT_OF_OTHER_CLIENT_MESSAGE = "O Equipamento selecionado não pertence ao Cliente informado.";
export const EQUIPMENT_OF_OTHER_PROJECT_MESSAGE = "O Equipamento selecionado não pertence ao Projeto informado.";
export const EQUIPMENT_OF_OTHER_AREA_MESSAGE = "O Equipamento selecionado não pertence à Unidade / Área informada.";

function validation(message: string): AppError {
  return new AppError(ErrorCode.Validation, message, { safeMessage: message });
}

const text = (value: string | null | undefined): string => String(value ?? "").trim();

/** O Cliente é obrigatório: sem ele o registro não teria a quem pertencer. */
export function requireAssetClientId(clientId: string | null | undefined): ClientId {
  const value = text(clientId);
  if (!value) throw validation(SELECT_CLIENT_MESSAGE);
  return value as ClientId;
}

/** Unidade/Área do equipamento — `unitAreaId` no cadastro atual, `areaId` nos antigos. */
export function equipmentAreaOf(equipment: TechnicalAssetEquipmentRef): string {
  return text(equipment.unitAreaId) || text(equipment.areaId);
}

/**
 * Recusa Projeto, Unidade/Área ou Equipamento de outro Cliente, Unidade/Área
 * de outro Projeto e Equipamento fora do Projeto/Unidade informados. Sem
 * `lookup` (composição antiga ou teste unitário do serviço) só o Cliente
 * obrigatório é exigido.
 */
export async function assertTechnicalAssetHierarchy(
  lookup: TechnicalAssetHierarchyLookup | null,
  links: TechnicalAssetLinks
): Promise<void> {
  const clientId = requireAssetClientId(links.clientId);
  if (!lookup) return;

  const projectId = text(links.projectId);
  if (projectId) {
    const project = await lookup.getProject(projectId);
    if (!project || text(project.clientId) !== clientId) throw validation(PROJECT_OF_OTHER_CLIENT_MESSAGE);
  }

  const areaId = text(links.areaId);
  if (areaId) {
    const unitArea = await lookup.getUnitArea(areaId);
    if (!unitArea || text(unitArea.clientId) !== clientId) throw validation(AREA_OF_OTHER_CLIENT_MESSAGE);
    if (projectId && text(unitArea.projectId) !== projectId) throw validation(AREA_OF_OTHER_PROJECT_MESSAGE);
  }

  const equipmentId = text(links.equipmentId);
  if (equipmentId) {
    const equipment = await lookup.getEquipment(clientId, equipmentId as EquipmentId);
    if (!equipment || text(equipment.clientId) !== clientId) throw validation(EQUIPMENT_OF_OTHER_CLIENT_MESSAGE);
    if (projectId && text(equipment.projectId) && text(equipment.projectId) !== projectId) {
      throw validation(EQUIPMENT_OF_OTHER_PROJECT_MESSAGE);
    }
    const equipmentArea = equipmentAreaOf(equipment);
    if (areaId && equipmentArea && equipmentArea !== areaId) throw validation(EQUIPMENT_OF_OTHER_AREA_MESSAGE);
  }
}

/**
 * O ativo pertence ao Projeto? Pelo `projectId` gravado nele ou, nos
 * cadastros anteriores a este campo, pelo Projeto do equipamento vinculado.
 */
export function belongsToProject(
  asset: { projectId?: string | null; equipmentId: string | null },
  projectId: string,
  equipmentProjectOf: (equipmentId: string) => string | null
): boolean {
  if (asset.projectId) return asset.projectId === projectId;
  return !!asset.equipmentId && equipmentProjectOf(asset.equipmentId) === projectId;
}
